import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ScrollText, Lock } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TermoTexto from "@/components/TermoTexto";
import { TERMO_VERSAO } from "@/lib/termoConteudo";
import logoMark from "@/assets/logo-mark.png";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export function maskCpf(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, "$1.$2").replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1-$2");
}

export function cpfValido(v) {
  const d = (v || "").replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const n of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(d[i]) * (n + 1 - i);
    if (((soma * 10) % 11) % 10 !== Number(d[n])) return false;
  }
  return true;
}

// Lote 87 — conclusão do cadastro: nome + CPF + OAB (só quando a conta ainda não tem) + Termo com
// rolagem obrigatória. Quem já tem tudo e só precisa aceitar uma versão nova do Termo vê apenas o Termo.
export default function CadastroAceite() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const falta = user?.cadastro_pendente || [];
  const precisaCpf = falta.includes("cpf");
  const cpfOpcional = !!user?.cpf_opcional; // conta antiga: só o Termo é obrigatório; CPF pode ser informado já
  const mostraCpf = precisaCpf || cpfOpcional;
  const precisaOab = falta.includes("oab");
  const soTermo = falta.length === 1 && falta[0] === "termo";
  const [nome, setNome] = useState(user?.name || "");
  const [cpf, setCpf] = useState("");
  const [oabNum, setOabNum] = useState("");
  const [oabUf, setOabUf] = useState("SP");
  const [leuTudo, setLeuTudo] = useState(false);
  const [aceito, setAceito] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const boxRef = useRef(null);

  const verificaRolagem = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setLeuTudo(true);
  }, []);
  useEffect(() => { verificaRolagem(); }, [verificaRolagem]);

  const nomeJaOk = (user?.name || "").trim().split(/\s+/).length >= 2 && !falta.includes("nome");
  const nomeOk = nome.trim().split(/\s+/).length >= 2 && nome.trim().length >= 5;
  const cpfOk = precisaCpf ? cpfValido(cpf) : (!cpf || cpfValido(cpf));
  const oabOk = !precisaOab || (oabNum.length >= 2 && oabNum.length <= 7);
  const podeEnviar = aceito && leuTudo && nomeOk && cpfOk && oabOk && !enviando;

  const enviar = async (e) => {
    e.preventDefault();
    if (!podeEnviar) return;
    setEnviando(true);
    try {
      await http.post("/auth/cadastro", {
        nome: nome.trim(),
        cpf: mostraCpf && cpf ? cpf : null,
        oab_numero: precisaOab ? oabNum : null,
        oab_uf: precisaOab ? oabUf : null,
        aceite: true,
        termo_versao: TERMO_VERSAO,
        rolou_ate_o_fim: true,
      });
      await checkAuth();
      toast.success("Cadastro concluído");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível concluir o cadastro");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-5 py-8" data-testid="cadastro-aceite">
      <form onSubmit={enviar} className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoMark} alt="" className="w-9 h-9 rounded-md" />
            <span className="font-display text-xl font-bold"><span className="text-gold-600">IA</span>pauta</span>
          </div>
          <button type="button" onClick={logout} className="text-xs text-stone-500 hover:text-stone-900">Sair</button>
        </div>

        <header>
          <h1 className="font-display text-2xl font-bold text-stone-900">
            {soTermo ? "Termo de Uso e Aviso de Privacidade" : "Conclua o seu cadastro"}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {soTermo
              ? "Leia até o fim e aceite para continuar usando o IApauta."
              : "Confirme seus dados e leia o Termo de Uso até o fim para liberar o aceite."}
          </p>
        </header>

        <section className="space-y-4">
          {!nomeJaOk && (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Nome completo</Label>
              <Input data-testid="cad-nome" value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" />
            </div>
          )}
          {mostraCpf ? (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">CPF{!precisaCpf && " (opcional por enquanto)"}</Label>
              <Input
                data-testid="cad-cpf" inputMode="numeric" placeholder="000.000.000-00" value={cpf}
                onChange={(e) => setCpf(maskCpf(e.target.value))}
              />
              {cpf.length === 14 && !cpfValido(cpf) && (
                <p className="text-xs text-rose-600 mt-1" data-testid="cad-cpf-erro">CPF inválido — confira os números.</p>
              )}
              <p className="text-[11px] text-stone-400 mt-1">
                Usado só para identificar você e evitar contas duplicadas. Fica cifrado e nunca é enviado à IA nem por e-mail.
              </p>
            </div>
          ) : (
            <p className="text-xs text-stone-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> CPF já cadastrado ({user?.cpf_masked}).</p>
          )}
          {precisaOab ? (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Sua OAB</Label>
              <div className="flex gap-3">
                <Input
                  data-testid="cad-oab-num" className="flex-1" inputMode="numeric" placeholder="Número (ex: 252280)"
                  value={oabNum} onChange={(e) => setOabNum(e.target.value.replace(/\D/g, "").slice(0, 7))}
                />
                <div className="w-24">
                  <Select value={oabUf} onValueChange={setOabUf}>
                    <SelectTrigger data-testid="cad-oab-uf"><SelectValue /></SelectTrigger>
                    <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-stone-400 mt-1">
                Depois de cadastrada, a OAB fica travada. Para incluir outra, é preciso pedir ao administrador.
              </p>
            </div>
          ) : (
            <p className="text-xs text-stone-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> OAB já cadastrada e travada.</p>
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-1.5">
              <ScrollText className="w-4 h-4 text-stone-500" /> Termo de Uso e Aviso de Privacidade <span className="text-xs font-normal text-stone-400">v{TERMO_VERSAO}</span>
            </h2>
            <span className={`text-xs ${leuTudo ? "text-emerald-700" : "text-stone-400"}`} data-testid="termo-status">
              {leuTudo ? "Leitura concluída" : "Role até o fim"}
            </span>
          </div>
          <div
            ref={boxRef}
            onScroll={verificaRolagem}
            data-testid="termo-caixa"
            tabIndex={0}
            className="h-[45vh] min-h-[260px] overflow-y-auto border border-stone-300 rounded-md p-4 bg-stone-50"
          >
            <TermoTexto />
          </div>
          <label className={`flex items-start gap-2 text-sm ${leuTudo ? "text-stone-800" : "text-stone-400"}`}>
            <input
              type="checkbox" data-testid="termo-aceite" className="mt-1" disabled={!leuTudo}
              checked={aceito} onChange={(e) => setAceito(e.target.checked)}
            />
            <span>Li o Termo inteiro e aceito.</span>
          </label>
        </section>

        <Button data-testid="cad-concluir" type="submit" disabled={!podeEnviar} className="w-full bg-emerald-brand text-white gap-2">
          {enviando && <Loader2 className="w-4 h-4 animate-spin" />} Concluir cadastro
        </Button>
      </form>
    </div>
  );
}
